import { useState, useRef } from 'react';
import useEnhancedEffect from '@lmig/dotcom-aspect-hooks/useEnhancedEffect.ts';

/**
 * Binds to an element in order to measure it's size and/or offset.
 *
 */
const useMeasure = () => {
  const rafCallbackRef = useRef();
  const measuredElemRef = useRef();
  const [bounds, set] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    scroll: {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    },
    offset: {
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    },
  });
  const [ro, setRo] = useState(null);

  useEnhancedEffect(() => {
    const setObserver = async () => {
      if (!('ResizeObserver' in window)) {
        const resizeObserver = await import('@juggle/resize-observer');
        window.ResizeObserver = resizeObserver.ResizeObserver;
      }

      setRo(
        new window.ResizeObserver(([entry]) => {
          if (rafCallbackRef.current) {
            window.cancelAnimationFrame(rafCallbackRef.current);
          }

          rafCallbackRef.current = window.requestAnimationFrame(() => {
            set({
              left: entry.contentRect.left,
              top: entry.contentRect.top,
              width: entry.contentRect.width,
              height: entry.contentRect.height,
              scroll: {
                left: measuredElemRef.current.scrollLeft,
                top: measuredElemRef.current.scrollTop,
                width: measuredElemRef.current.scrollWidth,
                height: measuredElemRef.current.scrollHeight,
              },
              offset: {
                left: measuredElemRef.current.offsetLeft,
                top: measuredElemRef.current.offsetTop,
                width: measuredElemRef.current.offsetWidth,
                height: measuredElemRef.current.offsetHeight,
              },
            });
          });
        }),
      );
    };

    setObserver();
  }, []);

  useEnhancedEffect(() => {
    if (measuredElemRef.current && ro) {
      ro.observe(measuredElemRef.current);
    }

    return () => ro && ro.disconnect();
  }, [ro]);

  return [{ ref: measuredElemRef }, bounds];
};

export default useMeasure;
